import React, { FC, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import Form from '../../../../components/Form';
import strings from '../../../../common/Translation/Translate';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { removeErrorFieldsFromValues } from '../../../../utils/validators';
import { SendThankYouEmailForm } from './SendThankYouEmailForm';
import { URLS } from '../../../../utils/constants/urls';
import axiosInstance from '../../../../utils/axios';
import PageLoader from '../../../../components/PageLoader';
import Notification from '../../../../components/Notification';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface Props {
  bookingLineItem: any;
  invoice?: {
    referenceNo?: string | null;
    formattedTotalCost?: string | null;
  };
  handleCatch: (error: any) => void;
  setOpenDrawer: (open: boolean) => void;
}

const SendEmail: FC<Props> = ({ bookingLineItem, invoice, handleCatch, setOpenDrawer }) => {
  const theme = useTheme();
  let formRef: RefObject<Form | null | undefined> = useRef();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [response, setResponse] = useState<any>();
  const [includeInvoice, setIncludeInvoice] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState<{ display: boolean; severity: 'error' | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });
  /** Sync guard so a second Send click in the same tick cannot start another POST. */
  const savingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    setResponse({
      to: bookingLineItem?.booking?.email,
    });
  }, [bookingLineItem]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selected.length) return;
    const tooLarge = selected.find(file => file.size > MAX_FILE_BYTES);
    if (tooLarge) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.booking_logs_file_too_large,
      });
      return;
    }
    setFiles(prev => {
      const byName = new Map(prev.map(file => [file.name, file]));
      selected.forEach(file => byName.set(file.name, file));
      return Array.from(byName.values());
    });
  };

  const handleSave = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    setIsSending(true);

    try {
      const data = formRef.current?.getFormData();

      const isFormValid = data?.isFormValid;
      if (!isFormValid) {
        setHasError(true);
        return;
      }

      const cleanedData = removeErrorFieldsFromValues(data?.formData);

      const formData = new FormData();
      formData.append('to', cleanedData.to);
      formData.append('cc', cleanedData.cc ?? '');
      formData.append('bcc', cleanedData.bcc ?? '');
      formData.append('isAttachInvoice', String(includeInvoice));
      if (!files.length) {
        formData.append('attachments', [] as any);
      } else {
        files.forEach(file => {
          formData.append('attachments', file);
        });
      }

      setIsLoading(true);
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const { status } = await axiosInstance.post(
        URLS.sendThankYouEmailAfterActualization(bookingLineItem.id),
        formData,
        config,
      );
      if (status === 200) {
        setOpenDrawer(false);
        setHasError(false);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      savingRef.current = false;
      setIsSending(false);
      setIsLoading(false);
    }
  };

  const invoiceNumber = invoice?.referenceNo?.trim() || '—';
  const invoiceAmount = invoice?.formattedTotalCost?.trim() || '—';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        height: '100%',
      }}>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity ?? 'error'}
          closeNotification={open => setMessage(prev => ({ ...prev, display: open }))}
          autoHideDuration={2000}
        />
      )}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, pt: 2, pb: 1 }}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Form
            hasError={hasError}
            ref={formRef as RefObject<Form>}
            model={SendThankYouEmailForm(strings)}
            values={response}
          />
        </Grid>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeInvoice}
                onChange={e => setIncludeInvoice(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {strings.actualization_attach_invoice}
              </Typography>
            }
            sx={{ ml: 0, mr: 0 }}
          />
          {includeInvoice && (
            <Paper
              variant="outlined"
              sx={{
                mt: 0.75,
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                bgcolor: 'action.hover',
              }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>
                {invoiceNumber}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, mt: 0.25 }}>
                {invoiceAmount}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.3, mt: 0.25 }}>
                {strings.actualization_latest_invoice}
              </Typography>
            </Paper>
          )}
        </Box>

        <input ref={fileInputRef} type="file" hidden multiple onChange={handleFileChange} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1.5,
            flexWrap: 'wrap',
          }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}>
            {strings.actualization_attach_file}
          </Button>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {strings.booking_logs_max_file_size}
          </Typography>
          {files.map(file => (
            <Chip
              key={file.name}
              size="small"
              label={file.name}
              onDelete={() => setFiles(prev => prev.filter(item => item.name !== file.name))}
              deleteIcon={<CloseIcon />}
              sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 1.5,
          pb: 2,
          mt: 1,
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
        }}>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="primary" onClick={() => setOpenDrawer(false)} disabled={isLoading || isSending}>
            {strings.cancelText}
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={e => handleSave(e)}
            disabled={isLoading || isSending}>
            {strings.bookings_text_send_email}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default SendEmail;
